import { DiContainer, Injectable, InjectByToken } from '@jack-henry/frontend-utils/di';
import { exists, isNonEmptyString, SessionStorageService, WindowService } from '@treasury/utils';
import {
    AppType,
    AppTypeToken,
    BACKOFFICE_API,
    BuildType,
    BuildTypeToken,
    CHANNEL_API,
    Environment,
    InjectedWindow,
    INSTITUTION_ID,
} from './configuration.types';

// this code exists because Vite does not support CommonJS imports in build mode
// even with the viteCommonjs plugin that supports it in dev server;
// for now, this is a stopgap that simply appears to have no vanity URLs
// TODO: add plugin that supports CommonJS to Vite config
let vanityUrlMap = new Map<string, string>();
try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { vanityUrlToIdMap } = require('./vanity-url-map');
    vanityUrlMap = vanityUrlToIdMap;
} catch {
    // eslint-disable-next-line no-console
    console.warn(
        'Could not load vanity URL map because require was not defined. Defaulting to empty map.'
    );
}

/**
 * Service exposing information used for bootstrapping or
 * otherwise configuring channel and BO applications.
 */
@Injectable()
export class ConfigurationService {
    constructor(
        @InjectByToken(AppTypeToken) public readonly app: AppType,
        @InjectByToken(BuildTypeToken) public readonly buildType: BuildType,
        private readonly window: WindowService,
        private readonly session: SessionStorageService
    ) {
        if (app === AppType.Unknown) {
            throw new Error('Could not determine application type.');
        }
    }

    /**
     * Convenience method for locations where DI isn't available.
     */
    public static async getInstance() {
        return (await DiContainer.getInstance()).get(ConfigurationService);
    }

    public readonly uisRedirectUrl = this.generateSsoCallbackUrl;

    /**
     * The API path used when the client is deployed together with the API on the same domain.
     */
    private readonly apiPath = this.app === AppType.Channel ? CHANNEL_API : BACKOFFICE_API;

    public get institutionId() {
        const path = this.window.location.pathname.split('/').filter(isNonEmptyString);
        if (!path) {
            throw new Error('Incomplete path. Unable to determine institutionId.');
        }
        if (this.app === AppType.Channel) {
            return path[0];
        }
        if (this.app === AppType.Pwa) {
            return path[1];
        }
        if (this.app === AppType.BackOffice) {
            return this.session.get(INSTITUTION_ID) ?? '';
        }
        return '';
    }

    public set institutionId(value: string) {
        if (exists(value)) {
            this.session.set(INSTITUTION_ID, value);
        }
    }

    /**
     * The root URL prepended to all calls made to API endpoints.
     */
    public get apiRoot() {
        const { apiRoot } = this.injectedConfig;

        return isNonEmptyString(apiRoot) ? apiRoot : `${this.baseUrl}/${this.apiPath}`;
    }

    public get analyticsToken() {
        return this.injectedConfig.analyticsToken;
    }

    /**
     * The base path of the application.
     */
    public get basePath() {
        return this.injectedConfig.basePath ?? '/';
    }

    /**
     * Whether the application is authenticated using banno authentication.
     */
    public get bannoAuthEnabled() {
        const value = this.injectedConfig.bannoAuthEnabled;
        if (typeof value === 'boolean') {
            return value;
        }
        if (typeof value === 'string') {
            const stringValue = value as string;
            if (stringValue.trim().toLowerCase() === 'true') {
                return true;
            }
            if (stringValue.trim().toLowerCase() === 'false') {
                return false;
            }
        }
        return this.injectedConfig.bannoAuthEnabled ?? false;
    }

    public get environment() {
        if (this.injectedMetadata.isLocal) {
            return Environment.Local;
        }

        const { hostname } = this.window.location;
        if (hostname) {
            const vanityUrls = Array.from(vanityUrlMap.keys());
            if (vanityUrls.some(url => hostname === url)) {
                return Environment.Production;
            }

            switch (hostname) {
                case '127.0.0.1':
                case 'localhost':
                    return Environment.Local;
                case 'treasury.dev.jackhenry.com':
                case 'treasury-channel.banno-development.com':
                case 'treasurybackoffice.dev.jackhenry.com':
                case 'platform.banno-development.com':
                    return Environment.Dev;
                case 'treasury.stg.jackhenry.com':
                case 'treasury-channel.banno-staging.com':
                case 'treasurybackoffice.stg.jackhenry.com':
                case 'platform.banno-staging.com':
                    return Environment.Staging;
                case 'treasury.jackhenry.com':
                case 'treasurybackoffice.jackhenry.com':
                    return Environment.Production;
                default:
                    return Environment.Unknown;
            }
        }

        return Environment.Unknown;
    }

    /**
     * The client-side ID token used by the feature flag SDK.
     */
    public get ffToken() {
        // maps an `Environment` to a LD Client-side ID
        // https://docs.launchdarkly.com/sdk/concepts/client-side-server-side#client-side-id
        switch (this.environment) {
            case Environment.Production:
                return '6633bf61801694108e04e3c2';
            case Environment.Dev:
                return '6633c04c1a652f0fc78187eb';
            case Environment.Local:
                return '665e22a09e41bb108d27a538';
            case Environment.Staging:
                return '66858f281005aa0f93eacc22';
            default:
                throw new Error('Could not retrieve LD SDK token.');
        }
    }

    public get loggingToken() {
        return this.injectedConfig.loggingToken;
    }

    public get remoteDepositToken() {
        // maps an `Environment` to a Remote Deposit SDK token
        switch (this.environment) {
            case Environment.Production:
            case Environment.Dev:
            case Environment.Staging:
                // case Environment.Local:
                return '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';
            case Environment.Local:
                return 'eyJzaWduYXR1cmUiOiJRd0x2QzdlNEZ1eHp0UVg5YkwrM0dZR21KK1pQQmY4YUJsM3E4amtLSitlUzNBd3ZYeFhkaE9jcE9FLzY1Q1o5MXJuUlcrNmFZNWtYRkFHNFVzOWVOSlpUYVlrQnZzaXZLZjF5U1E3T0ZFUFdsMEdoNU44NW9uV2JVMlErYkJKaFY2bXNlb0dKM3dtT01Xc1hCNjlBb240RTE3TGRHdzNJMmJiWUgxZkxNOE9sVjhtc3U3WHlnUnhkcVBrMDROZlp0cFBGR0h2bEVWbjF5TEZzRVZVUmZCaXhZM1BTK0g5YzU3SnAxVWFHWDJFdU05S3g0c05rbkc5SkVRZEF3cGVnSmtKODV2QTdua1prUDNDbFdxaTlZSW5Md2pmQ0VVSHFtWnl3L0gxbmFTeUxKUURoNHdGbkJmbFZieGpXblFPV1c5djBJREQva29aVFVIR1V0di9TOUE9PSIsInNpZ25lZCI6eyJleHBpcnkiOiIyMDI2LTA3LTMxIiwiZmVhdHVyZXMiOlsiZGVwb3NpdCIsImVtIl0sImFwcGxpY2F0aW9uSWRlbnRpZmllciI6eyJ3ZWIiOlsiMTAuMC4xLjE1IiwiMTkyLjE2OC4xLjE1Il19LCJncG8iOiI0NDciLCJvcmdhbml6YXRpb24iOiJNaXRlayBTeXN0ZW1zIEluYy4iLCJ2ZXJzaW9uIjoiMS4wIn19';
            default:
                throw new Error('Could not retrieve RDC SDK token.');
        }
    }

    public get version() {
        return this.injectedMetadata.version;
    }

    private get baseUrl() {
        const { protocol, hostname, port } = window.location;
        let url = `${protocol}//${hostname}`;

        if (port) {
            url += `:${port}`;
        }

        return url;
    }

    private get generateSsoCallbackUrl() {
        let { host } = window.location;
        if (this.app === AppType.Pwa) {
            host = `${host}/pwa`;
        }
        return `${window.location.protocol}//${host}/${this.institutionId}/login/sso`;
    }

    private get injectedConfig() {
        return (window as unknown as InjectedWindow).config ?? {};
    }

    private get injectedMetadata() {
        return (window as unknown as InjectedWindow).treasury ?? {};
    }
}

const buildType = BuildTypeToken.getValue() || BuildType.Unknown;
const windowService = new WindowService();

/**
 * @deprecated Prefer referencing `apiRoot` through the configuration service.
 */
export const channelApiRoot = new ConfigurationService(
    AppType.Channel,
    buildType,
    windowService,
    new SessionStorageService(windowService)
).apiRoot;

/**
 * @deprecated Prefer referencing `apiRoot` through the configuration service.
 */
export const boApiRoot = new ConfigurationService(
    AppType.BackOffice,
    buildType,
    windowService,
    new SessionStorageService(windowService)
).apiRoot;
